import { DiContainer, Injectable, InjectByToken } from '@jack-henry/frontend-utils/di';
import { exists, isNonEmptyString, SessionStorageService, WindowService } from '@treasury/utils';
import {
    AppType,
    AppTypeToken,
    BACKOFFICE_API,
    BuildType,
    BuildTypeToken,
    CHANNEL_API,
    Environment,
    InjectedWindow,
    INSTITUTION_ID,
} from './configuration.types';

// this code exists because Vite does not support CommonJS imports in build mode
// even with the viteCommonjs plugin that supports it in dev server;
// for now, this is a stopgap that simply appears to have no vanity URLs
// TODO: add plugin that supports CommonJS to Vite config
let vanityUrlMap = new Map<string, string>();
try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { vanityUrlToIdMap } = require('./vanity-url-map');
    vanityUrlMap = vanityUrlToIdMap;
} catch {
    // eslint-disable-next-line no-console
    console.warn(
        'Could not load vanity URL map because require was not defined. Defaulting to empty map.'
    );
}

/**
 * Service exposing information used for bootstrapping or
 * otherwise configuring channel and BO applications.
 */
@Injectable()
export class ConfigurationService {
    constructor(
        @InjectByToken(AppTypeToken) public readonly app: AppType,
        @InjectByToken(BuildTypeToken) public readonly buildType: BuildType,
        private readonly window: WindowService,
        private readonly session: SessionStorageService
    ) {
        if (app === AppType.Unknown) {
            throw new Error('Could not determine application type.');
        }
    }

    /**
     * Convenience method for locations where DI isn't available.
     */
    public static async getInstance() {
        return (await DiContainer.getInstance()).get(ConfigurationService);
    }

    public readonly uisRedirectUrl = this.generateSsoCallbackUrl;

    /**
     * The API path used when the client is deployed together with the API on the same domain.
     */
    private readonly apiPath = this.app === AppType.Channel ? CHANNEL_API : BACKOFFICE_API;

    public get institutionId() {
        const path = this.window.location.pathname.split('/').filter(isNonEmptyString);
        if (!path) {
            throw new Error('Incomplete path. Unable to determine institutionId.');
        }
        if (this.app === AppType.Channel) {
            return path[0];
        }
        if (this.app === AppType.Pwa) {
            return path[1];
        }
        if (this.app === AppType.BackOffice) {
            return this.session.get(INSTITUTION_ID) ?? '';
        }
        return '';
    }

    public set institutionId(value: string) {
        if (exists(value)) {
            this.session.set(INSTITUTION_ID, value);
        }
    }

    /**
     * The root URL prepended to all calls made to API endpoints.
     */
    public get apiRoot() {
        const { apiRoot } = this.injectedConfig;

        return isNonEmptyString(apiRoot) ? apiRoot : `${this.baseUrl}/${this.apiPath}`;
    }

    public get analyticsToken() {
        return this.injectedConfig.analyticsToken;
    }

    /**
     * The base path of the application.
     */
    public get basePath() {
        return this.injectedConfig.basePath ?? '/';
    }

    /**
     * Whether the application is authenticated using banno authentication.
     */
    public get bannoAuthEnabled() {
        const value = this.injectedConfig.bannoAuthEnabled;
        if (typeof value === 'boolean') {
            return value;
        }
        if (typeof value === 'string') {
            const stringValue = value as string;
            if (stringValue.trim().toLowerCase() === 'true') {
                return true;
            }
            if (stringValue.trim().toLowerCase() === 'false') {
                return false;
            }
        }
        return this.injectedConfig.bannoAuthEnabled ?? false;
    }

    public get environment() {
        if (this.injectedMetadata.isLocal) {
            return Environment.Local;
        }

        const { hostname } = this.window.location;
        if (hostname) {
            const vanityUrls = Array.from(vanityUrlMap.keys());
            if (vanityUrls.some(url => hostname === url)) {
                return Environment.Production;
            }

            switch (hostname) {
                case '127.0.0.1':
                case 'localhost':
                    return Environment.Local;
                case 'treasury.dev.jackhenry.com':
                case 'treasury-channel.banno-development.com':
                case 'treasurybackoffice.dev.jackhenry.com':
                case 'platform.banno-development.com':
                    return Environment.Dev;
                case 'treasury.stg.jackhenry.com':
                case 'treasury-channel.banno-staging.com':
                case 'treasurybackoffice.stg.jackhenry.com':
                case 'platform.banno-staging.com':
                    return Environment.Staging;
                case 'treasury.jackhenry.com':
                case 'treasurybackoffice.jackhenry.com':
                    return Environment.Production;
                default:
                    return Environment.Unknown;
            }
        }

        return Environment.Unknown;
    }

    /**
     * The client-side ID token used by the feature flag SDK.
     */
    public get ffToken() {
        // maps an `Environment` to a LD Client-side ID
        // https://docs.launchdarkly.com/sdk/concepts/client-side-server-side#client-side-id
        switch (this.environment) {
            case Environment.Production:
                return '6633bf61801694108e04e3c2';
            case Environment.Dev:
                return '6633c04c1a652f0fc78187eb';
            case Environment.Local:
                return '665e22a09e41bb108d27a538';
            case Environment.Staging:
                return '66858f281005aa0f93eacc22';
            default:
                throw new Error('Could not retrieve LD SDK token.');
        }
    }

    public get loggingToken() {
        return this.injectedConfig.loggingToken;
    }

    public get remoteDepositToken() {
        // maps an `Environment` to a Remote Deposit SDK token
        switch (this.environment) {
            case Environment.Production:
            case Environment.Dev:
            case Environment.Staging:
                // case Environment.Local:
                return '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';
            case Environment.Local:
                return 'eyJzaWduYXR1cmUiOiJRd0x2QzdlNEZ1eHp0UVg5YkwrM0dZR21KK1pQQmY4YUJsM3E4amtLSitlUzNBd3ZYeFhkaE9jcE9FLzY1Q1o5MXJuUlcrNmFZNWtYRkFHNFVzOWVOSlpUYVlrQnZzaXZLZjF5U1E3T0ZFUFdsMEdoNU44NW9uV2JVMlErYkJKaFY2bXNlb0dKM3dtT01Xc1hCNjlBb240RTE3TGRHdzNJMmJiWUgxZkxNOE9sVjhtc3U3WHlnUnhkcVBrMDROZlp0cFBGR0h2bEVWbjF5TEZzRVZVUmZCaXhZM1BTK0g5YzU3SnAxVWFHWDJFdU05S3g0c05rbkc5SkVRZEF3cGVnSmtKODV2QTdua1prUDNDbFdxaTlZSW5Md2pmQ0VVSHFtWnl3L0gxbmFTeUxKUURoNHdGbkJmbFZieGpXblFPV1c5djBJREQva29aVFVIR1V0di9TOUE9PSIsInNpZ25lZCI6eyJleHBpcnkiOiIyMDI2LTA3LTMxIiwiZmVhdHVyZXMiOlsiZGVwb3NpdCIsImVtIl0sImFwcGxpY2F0aW9uSWRlbnRpZmllciI6eyJ3ZWIiOlsiMTAuMC4xLjE1IiwiMTkyLjE2OC4xLjE1Il19LCJncG8iOiI0NDciLCJvcmdhbml6YXRpb24iOiJNaXRlayBTeXN0ZW1zIEluYy4iLCJ2ZXJzaW9uIjoiMS4wIn19';
            default:
                throw new Error('Could not retrieve RDC SDK token.');
        }
    }

    public get version() {
        return this.injectedMetadata.version;
    }

    private get baseUrl() {
        const { protocol, hostname, port } = window.location;
        let url = `${protocol}//${hostname}`;

        if (port) {
            url += `:${port}`;
        }

        return url;
    }

    private get generateSsoCallbackUrl() {
        let { host } = window.location;
        if (this.app === AppType.Pwa) {
            host = `${host}/pwa`;
        }
        return `${window.location.protocol}//${host}/${this.institutionId}/login/sso`;
    }

    private get injectedConfig() {
        return (window as unknown as InjectedWindow).config ?? {};
    }

    private get injectedMetadata() {
        return (window as unknown as InjectedWindow).treasury ?? {};
    }
}

const buildType = BuildTypeToken.getValue() || BuildType.Unknown;
const windowService = new WindowService();

/**
 * @deprecated Prefer referencing `apiRoot` through the configuration service.
 */
export const channelApiRoot = new ConfigurationService(
    AppType.Channel,
    buildType,
    windowService,
    new SessionStorageService(windowService)
).apiRoot;

/**
 * @deprecated Prefer referencing `apiRoot` through the configuration service.
 */
export const boApiRoot = new ConfigurationService(
    AppType.BackOffice,
    buildType,
    windowService,
    new SessionStorageService(windowService)
).apiRoot;
